import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Globe, Search, ChevronRight } from 'lucide-react';
import { useCountry } from '../contexts/CountryContext';
import { useLanguage } from '../contexts/LanguageContext';
import { CountryFlag } from './CountryFlag';

export default function CountrySelector({ onSelect }) {
  const navigate = useNavigate();
  const { countries, selectCountry, loading } = useCountry();
  const { language } = useLanguage();
  const [search, setSearch] = useState('');

  const filteredCountries = countries.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.name_fr?.toLowerCase().includes(search.toLowerCase()) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (code) => {
    selectCountry(code);
    if (onSelect) onSelect(code);
    // Navigate to home after selection
    navigate('/');
  };

  // Featured countries (most populous)
  const featuredCodes = ['NG', 'EG', 'ET', 'CD', 'ZA', 'TZ', 'KE', 'UG', 'DZ', 'MA', 'GH', 'CM'];
  const featuredCountries = countries.filter(c => featuredCodes.includes(c.code));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12" data-testid="country-selector">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Globe className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">
            {language === 'fr' ? 'Sélectionnez Votre Pays' : 'Select Your Country'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'fr' 
              ? 'Choisissez votre pays pour accéder aux enquêtes locales' 
              : 'Choose your country to access local governance surveys'}
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-8 max-w-md mx-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={language === 'fr' ? 'Rechercher un pays...' : 'Search for a country...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Featured Countries */}
        {!search && (
          <div className="mb-8">
            <h2 className="text-sm font-medium text-muted-foreground mb-4">
              {language === 'fr' ? 'Pays en Vedette' : 'Featured Countries'}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {featuredCountries.map((country) => (
                <Button
                  key={country.code}
                  variant="outline"
                  className="h-auto py-3 justify-start gap-3 hover:bg-primary/5 hover:border-primary"
                  onClick={() => handleSelect(country.code)}
                >
                  <CountryFlag code={country.code} width={28} />
                  <span className="truncate">{language === 'fr' ? country.name_fr : country.name}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* All Countries */}
        <div>
          <h2 className="text-sm font-medium text-muted-foreground mb-4">
            {search 
              ? `${filteredCountries.length} ${language === 'fr' ? 'résultats' : 'results'}`
              : language === 'fr' ? 'Tous les Pays Africains' : 'All African Countries'}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {filteredCountries.map((country) => (
              <Button
                key={country.code}
                variant="ghost"
                className="h-auto py-2 justify-start gap-2 text-sm hover:bg-muted"
                onClick={() => handleSelect(country.code)}
              >
                <CountryFlag code={country.code} width={20} />
                <span className="truncate">{language === 'fr' ? country.name_fr : country.name}</span>
              </Button>
            ))}
          </div>
        </div>

        {filteredCountries.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {language === 'fr' ? 'Aucun pays trouvé' : 'No countries found'}
            </p>
          </div>
        )}

        {/* Note */}
        <Card className="mt-8">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="mt-0.5">
                {language === 'fr' ? 'Note' : 'Note'}
              </Badge>
              <p className="text-sm text-muted-foreground">
                {language === 'fr' 
                  ? 'Les données régionales détaillées sont actuellement disponibles pour le Nigeria. D\'autres pays seront ajoutés progressivement.'
                  : 'Detailed regional data is currently available for Nigeria. Other countries will be added progressively.'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
